import React, { useState, useEffect } from 'react';
import { get_all_artists_api, edit_artist_by_admin_api, get_new_artist_ids_hint_api, BASE_URL } from '../controllers/dataController';
import { FaTimes, FaSearch } from 'react-icons/fa';
import { Button } from "./custom_ui/button.tsx";
import SearchableSelect from './SearchableSelect';
import { useAlert } from '../context/AlertContext';

const EditArtistForm = ({ setShowEditArtistForm, searchArtistId }) => {
    const [artists, setArtists] = useState([]);
    const [selectedArtist, setSelectedArtist] = useState(null);
    const [formData, setFormData] = useState({ pk: '', name: '', email: '', password: '', artist_ids: [] });
    const [artistIdHints, setArtistIdHints] = useState({});
    const [customIdInput, setCustomIdInput] = useState("");
    
    const { showAlert } = useAlert();

    useEffect(() => {
        fetchArtists();
    }, []);

    useEffect(() => {
        if (formData.name && formData.name.length > 2) {
            get_new_artist_ids_hint_api(formData.name).then((response) => {
                setArtistIdHints(response.new_artist_ids);
            }).catch((error) => {
                console.error("Failed to get hints:", error);
            });
        } else {
            setArtistIdHints({});
        }
    }, [formData.name]);

    const fetchArtists = () => {
        get_all_artists_api().then(response => {
            setArtists(response);
        }).catch(error => {
            console.error("Failed to get all artists:", error);
            showAlert('Failed to load artists.', 'danger');
        });
    }

    const handleArtistSelect = (e) => {
        const artistPk = e.target.value;
        if (!artistPk) {
            setSelectedArtist(null);
            setFormData({ pk: '', name: '', email: '', password: '', artist_ids: [] });
            setArtistIdHints({});
            return;
        }
        const artist = artists.find(a => a.pk.toString() === artistPk);
        setSelectedArtist(artist);
        if (artist) {
            setFormData({
                pk: artist.pk,
                name: artist.name,
                email: artist.email,
                password: '', 
                artist_ids: artist.artist_ids || [] 
            });
        }
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleAddCustomId = () => {
        if (customIdInput && customIdInput.trim() !== "") {
            const newId = customIdInput.trim();
            if (!formData.artist_ids.includes(newId)) {
                setFormData(prev => ({
                    ...prev,
                    artist_ids: [...prev.artist_ids, newId]
                }));
                setCustomIdInput("");
            } else {
                showAlert("ID already added.", "warning");
            }
        }
    };

    const handleRemoveId = (idToRemove) => {
        setFormData(prev => ({
            ...prev,
            artist_ids: prev.artist_ids.filter(id => id !== idToRemove)
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Validation
        if (!formData.name || formData.name.trim() === "") {
            showAlert("Artist/Label Name is required.", "danger");
            return;
        }

        if (!formData.email || !formData.email.trim()) {
            showAlert("Email is required.", "danger");
            return;
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            showAlert("Email is invalid.", "danger");
            return;
        }

        if (formData.password && formData.password.trim() !== "") {
             if (formData.password.trim().length < 6) {
                showAlert("Password must be at least 6 characters.", "danger");
                return;
            }
        }

        if (!formData.artist_ids || formData.artist_ids.length === 0) {
            showAlert("Please select at least one artist ID.", "danger");
            return;
        }

        edit_artist_by_admin_api(formData).then(response => {
            showAlert('Artist updated successfully!', 'success');
            fetchArtists(); 
            setSelectedArtist(null);
            setFormData({ pk: '', name: '', email: '', password: '', artist_ids: [] });
            if (setShowEditArtistForm) {
                setShowEditArtistForm(false);
            }
        }).catch(error => {
            console.error("Failed to update artist:", error);
            showAlert(error.toString(), "danger");
        });
    };

    return (
        <div className="mt-4 p-4 w-3/4 rounded-md flex flex-col items-center">
            <div className="flex justify-between items-center w-100 mb-2"> 
                <h4 className="text-md font-semibold">Edit Existing Artist</h4>
                <button onClick={() => setShowEditArtistForm(false)} className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-600">
                    <FaTimes />
                </button>
            </div>
            
            <form onSubmit={handleSubmit} className="w-100">
                <div className="mb-4">
                    <label className="block text-gray-400 text-sm mb-2" htmlFor="artist-select">
                        Select Artist/Label
                    </label>
                    <select
                        id="artist-select"
                        onChange={handleArtistSelect}
                        value={selectedArtist ? selectedArtist.pk : ""}
                        className="w-full bg-[#2A2A40] border border-gray-600 text-white rounded-md py-2 px-5 focus:outline-none focus:border-purple-500"
                    >
                        <option value="">--Please choose an artist/label--</option>
                        {artists.map(artist => (
                            <option key={artist.pk} value={artist.pk}>{artist.name} ({artist.email})</option>
                        ))}
                    </select>
                </div>
                
                {selectedArtist && (
                    <>
                        <div className="mb-4">
                            <label className="block text-gray-400 text-sm mb-2" htmlFor="name">
                                Artist/Label Name
                            </label>
                            <input
                                type="text"
                                name="name"
                                id="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                className="w-full bg-[#2A2A40] border border-gray-600 text-white rounded-md py-2 px-3 focus:outline-none focus:border-purple-500"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-gray-400 text-sm mb-2" htmlFor="email">
                                Artist/Label Email
                            </label>
                            <input
                                type="email"
                                name="email"
                                id="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                className="w-full bg-[#2A2A40] border border-gray-600 text-white rounded-md py-2 px-3 focus:outline-none focus:border-purple-500"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-gray-400 text-sm mb-2" htmlFor="password">
                                Account Password (leave blank to keep current)
                            </label>
                            <input
                                type="password"
                                name="password"
                                id="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                className="w-full bg-[#2A2A40] border border-gray-600 text-white rounded-md py-2 px-3 focus:outline-none focus:border-purple-500"
                            />
                        </div>
                        
                        <div className="mb-4">
                            <label className="block text-gray-400 text-sm mb-2">Artist IDs</label>
                                                            <div className="flex flex-col gap-3">
                                                            {/* Searchable Select for Hints */}
                                                            {Object.keys(artistIdHints).length > 0 ? (
                                                                <SearchableSelect
                                                                    options={Object.keys(artistIdHints)
                                                                        .filter(id => !formData.artist_ids.includes(id))
                                                                        .map(id => ({ value: id, label: id }))
                                                                    }
                                                                    placeholder="Select from hinted IDs..."
                                                                    renderOption={(option) => (
                                                                        <div className="flex items-center justify-between w-full group">
                                                                            <span>{option.label}</span>
                                                                            <div className="flex items-center gap-2 ml-2">
                                                                                <div className="flex gap-1">
                                                                                    {artistIdHints[option.value] && artistIdHints[option.value].map((platform, i) => (
                                                                                        <img 
                                                                                            key={i} 
                                                                                            src={`${BASE_URL}/icons/${platform}/`} 
                                                                                            alt={platform}
                                                                                            className="w-5 h-5 rounded-full object-cover bg-white" 
                                                                                            title={platform}
                                                                                        />
                                                                                    ))}
                                                                                </div>
                                                                                {searchArtistId && (
                                                                                    <button 
                                                                                        type="button"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            searchArtistId(option.value);
                                                                                        }}
                                                                                        className="text-muted-foreground hover:text-white p-1 rounded-full hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity"
                                                                                        title="Search this ID"
                                                                                    >
                                                                                        <FaSearch size={12} />
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    onChange={(selectedId) => {
                                                                        if (!formData.artist_ids.includes(selectedId)) {
                                                                            setFormData(prev => ({
                                                                                ...prev,
                                                                                artist_ids: [...prev.artist_ids, selectedId]
                                                                            }));
                                                                        }
                                                                    }}
                                                                />                                ) : (
                                    <p className="text-gray-400 text-xs italic">Type artist name to see hinted IDs</p>
                                )}

                                {/* Selected IDs Chips */}
                                <div className="flex flex-wrap gap-2">
                                    {formData.artist_ids.map((id) => (
                                        <div key={id} className="flex items-center bg-[#2A2A40] border border-border/50 rounded-full px-3 py-1 text-xs">
                                            <span className="mr-2 font-medium">{id}</span>
                                            {/* Show icons if available in hints, else just text/generic */}
                                            <div className="flex mr-2">
                                                {artistIdHints[id] && artistIdHints[id].map((platform, pIndex) => (
                                                    <img 
                                                        key={pIndex} 
                                                        src={`${BASE_URL}/icons/${platform}/`} 
                                                        alt={platform} 
                                                        className="w-4 h-4 rounded-full -ml-1 border border-background"
                                                    />
                                                ))}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveId(id)}
                                                className="text-muted-foreground hover:text-destructive transition-colors"
                                            >
                                                <FaTimes size={10} />
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                {/* Custom ID Input */}
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        className="flex-1 bg-[#2A2A40] border border-gray-600 text-white rounded-md py-2 px-3 focus:outline-none focus:border-primary text-sm"
                                        placeholder="Enter custom artist ID"
                                        value={customIdInput}
                                        onChange={(e) => setCustomIdInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault(); // Prevent form submission
                                                handleAddCustomId();
                                            }
                                        }}
                                    />
                                    <button 
                                        type='button' 
                                        onClick={handleAddCustomId}
                                        className="text-sm text-primary hover:underline font-medium p-2"
                                    >
                                        Add
                                    </button>
                                </div>
                            </div>
                        </div>

                        <Button
                            className="w-full text-white font-bold py-3 px-4 rounded-md shadow-gold hover:shadow-gold/60 transition-all active:scale-[0.98]"
                            type="submit"
                        >
                            Save Changes
                        </Button>
                    </>
                )}
            </form>
        </div>
    );
};

export default EditArtistForm;
